import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Camera, X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

const CATEGORIES = [
  { id: "all", label: "All Photos" },
  { id: "gala", label: "Gala & Awards" },
  { id: "community", label: "Community Outreach" },
  { id: "ride", label: "Awareness Rides" },
  { id: "stadium", label: "Stadium Events" },
  { id: "law_enforcement", label: "First Responders" },
  { id: "media", label: "Media" }
];

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = activeCategory === "all" 
    ? images 
    : images.filter(img => img.category === activeCategory);

  const openLightbox = (image) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const navigateImage = (direction) => {
    const currentIndex = filteredImages.findIndex(img => img.image_id === selectedImage.image_id);
    let newIndex;
    
    if (direction === "next") {
      newIndex = currentIndex + 1 >= filteredImages.length ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex - 1 < 0 ? filteredImages.length - 1 : currentIndex - 1;
    }
    
    setSelectedImage(filteredImages[newIndex]);
  };

  const getImageUrl = (image) => {
    return image.url || `${API}/gallery/image/${image.image_id}`;
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="gradient-hero py-16" data-testid="gallery-hero">
        <div className="container-custom text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
            <Camera className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-heading-1 text-white mb-4">Photo Gallery</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Explore moments from our events, community outreach programs, and awareness campaigns
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 border-b bg-white sticky top-0 z-20" data-testid="gallery-filters">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-2">
            {CATEGORIES.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? "bg-deep-blue text-white shadow-md"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`filter-${category.id}`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 bg-slate-50" data-testid="gallery-grid">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="aspect-square bg-slate-200 rounded-lg animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredImages.map((image, index) => (
                <div
                  key={image.image_id}
                  className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
                  onClick={() => openLightbox(image)}
                  data-testid={`gallery-image-${image.image_id}`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <img
                    src={getImageUrl(image)}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white font-semibold text-lg mb-1">{image.title}</h3>
                      <p className="text-white/80 text-sm line-clamp-2">{image.description}</p>
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Play className="w-4 h-4 text-white" fill="white" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredImages.length === 0 && (
            <div className="text-center py-16">
              <Camera className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No photos found in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-deep-blue" data-testid="gallery-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold text-white font-heading">{images.length}</div>
              <div className="text-awareness-blue text-sm uppercase tracking-wide mt-1">Photos</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white font-heading">{CATEGORIES.length - 1}</div>
              <div className="text-awareness-blue text-sm uppercase tracking-wide mt-1">Categories</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white font-heading">15+</div>
              <div className="text-awareness-blue text-sm uppercase tracking-wide mt-1">Events</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white font-heading">1000+</div>
              <div className="text-awareness-blue text-sm uppercase tracking-wide mt-1">Lives Touched</div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl p-0 bg-black/95 border-none">
          {selectedImage && (
            <div className="relative">
              {/* Close button */}
              <button
                onClick={() => setLightboxOpen(false)}
                className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                data-testid="lightbox-close"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              {/* Navigation buttons */}
              <button
                onClick={() => navigateImage("prev")}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={() => navigateImage("next")}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>

              {/* Image */}
              <div className="flex items-center justify-center min-h-[60vh] max-h-[80vh] p-8">
                <img
                  src={getImageUrl(selectedImage)}
                  alt={selectedImage.title}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                />
              </div>

              {/* Caption */}
              <div className="p-6 text-center">
                <h3 className="text-white font-semibold text-xl mb-2">{selectedImage.title}</h3>
                <p className="text-white/70">{selectedImage.description}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
